import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, Send, Bot, User, Mic, Square, Loader2, Plus, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useDictation, streamChat } from "../../lib/aiClient";
import { Rich, ExportBar, ActionCard, splitAction } from "../../components/ai/AiMessage";
import AiTools from "./AiTools";

const AI_SUGGESTIONS = [
  "Liste mes loyers impayés sous forme de tableau",
  "Rédige un courrier de relance de loyer impayé",
  "Crée une tâche pour planifier le DPE du studio",
  "Résume ma situation financière de l'année",
];
const newId = () => `conv-${Date.now().toString(36)}`;

function SessionList({ sessions, current, onPick, onNew, onDelete }) {
  return (
    <div className="bg-white rounded-2xl border border-brand-soft p-4 flex flex-col min-h-0" data-testid="ai-sessions">
      <button onClick={onNew} className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-brand-greenDeep hover:bg-brand-green text-white text-sm font-semibold" data-testid="ai-new-conversation"><Plus className="w-4 h-4" /> Nouvelle conversation</button>
      <p className="text-xs font-semibold text-brand-green/50 mt-4 mb-2">Conversations précédentes</p>
      <div className="space-y-1 overflow-y-auto flex-1">
        {sessions.length === 0 && <p className="text-xs text-brand-green/40">Aucune conversation pour l'instant.</p>}
        {sessions.map((s) => (
          <div key={s.id} className={`group flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer ${s.id === current ? "bg-brand-lime/25" : "hover:bg-brand-softer"}`} onClick={() => onPick(s.id)} data-testid={`ai-session-${s.id}`}>
            <MessageSquare className="w-4 h-4 text-brand-green/40 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-brand-greenDeep truncate">{s.title}</p>
              <p className="text-[10px] text-brand-green/40">{new Date(s.last_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>
            </div>
            <button onClick={(e) => { e.stopPropagation(); onDelete(s.id); }} className="opacity-0 group-hover:opacity-100 text-brand-green/30 hover:text-red-500" data-testid={`ai-session-delete-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Bubble({ m, i, typing, question }) {
  const isUser = m.from === "user";
  const { body, action } = isUser ? { body: m.text, action: null } : splitAction(m.text || "");
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`ai-msg-${i}`}>
      <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${isUser ? "bg-brand-soft text-brand-green" : "bg-brand-greenDeep text-brand-lime"}`}>
        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
      </span>
      <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${isUser ? "bg-brand-lime text-brand-greenDeep rounded-tr-md whitespace-pre-line" : "bg-brand-softer text-brand-green rounded-tl-md"}`}>
        {isUser ? m.text : (body ? <Rich text={body} /> : (typing ? "…" : ""))}
        {!isUser && !typing && action && <ActionCard action={action} testid={`ai-action-${i}`} />}
        {!isUser && !typing && body && <ExportBar content={body} title={(question || "Réponse IA").slice(0, 60)} testid={`ai-export-${i}`} />}
      </div>
    </div>
  );
}

export default function AssistantIA({ embedded = false }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(() => localStorage.getItem("ai_current_session") || newId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const endRef = useRef(null);

  const loadSessions = useCallback(() => api.get("/ai/sessions").then((r) => setSessions(r.data || [])).catch(() => {}), []);
  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => {
    localStorage.setItem("ai_current_session", sid);
    api.get(`/ai/history/${sid}`).then((r) => setMessages((r.data || []).map((m) => ({ from: m.role === "user" ? "user" : "ai", text: m.text })))).catch(() => setMessages([]));
  }, [sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages, typing]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || typing) return;
    setInput("");
    setMessages((m) => [...m, { from: "user", text: question }, { from: "ai", text: "" }]);
    setTyping(true);
    try {
      await streamChat(sid, question, (d) => setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + d }; return c; }));
    } catch { toast.error("L'IA est indisponible, réessayez."); }
    finally { setTyping(false); loadSessions(); }
  };
  const dict = useDictation((t) => send(t));
  const remove = async (id) => {
    await api.delete(`/ai/sessions/${id}`).catch(() => {});
    if (id === sid) setSid(newId());
    loadSessions();
  };

  return (
    <div className="space-y-6">
    <div className={`grid lg:grid-cols-4 gap-6 ${embedded ? "h-[calc(100vh-220px)] min-h-[560px]" : "h-[calc(100vh-160px)]"}`} data-testid="ai-workspace">
      <SessionList sessions={sessions} current={sid} onPick={setSid} onNew={() => { setSid(newId()); setMessages([]); }} onDelete={remove} />
      <div className="lg:col-span-3 bg-white rounded-2xl border border-brand-soft flex flex-col overflow-hidden min-h-0">
        <div className="px-6 py-4 border-b border-brand-soft flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-brand-greenDeep flex items-center justify-center"><Sparkles className="w-5 h-5 text-brand-lime" /></span>
          <div>
            <p className="font-semibold text-brand-greenDeep">360immoinvest IA</p>
            <p className="text-xs text-brand-green/50">Questions sur votre espace, tableaux exportables, création de tâches et de courriers sur demande</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5" data-testid="ai-messages">
          {messages.length === 0 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="ai-suggestions">
              {AI_SUGGESTIONS.map((s) => <button key={s} onClick={() => send(s)} className="text-left px-4 py-3 rounded-xl bg-brand-softer hover:bg-brand-soft text-sm text-brand-green font-medium">{s}</button>)}
            </div>
          )}
          {messages.map((m, i) => <Bubble key={i} m={m} i={i} typing={typing && i === messages.length - 1} question={messages[i - 1]?.text} />)}
          <div ref={endRef} />
        </div>
        <div className="p-4 border-t border-brand-soft flex items-end gap-2">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1}
            placeholder={dict.rec ? "Je vous écoute… (cliquez sur stop pour envoyer)" : "Écrivez ou dictez votre question…"}
            className="flex-1 resize-none px-4 py-3 rounded-2xl bg-brand-softer outline-none text-sm focus:ring-2 focus:ring-brand-lime/40" data-testid="ai-input" />
          <button onClick={dict.rec ? dict.stop : dict.start} disabled={typing || dict.transcribing} title={dict.rec ? "Arrêter la dictée" : "Dicter"}
            className={`w-12 h-12 rounded-full flex items-center justify-center disabled:opacity-50 ${dict.rec ? "bg-red-500 text-white animate-pulse" : "bg-brand-softer hover:bg-brand-soft text-brand-greenDeep"}`} data-testid="ai-mic">
            {dict.transcribing ? <Loader2 className="w-5 h-5 animate-spin" /> : dict.rec ? <Square className="w-4 h-4" /> : <Mic className="w-5 h-5" />}
          </button>
          <button onClick={() => send()} disabled={typing || !input.trim()} className="w-12 h-12 rounded-full bg-brand-lime hover:bg-brand-limeDark flex items-center justify-center text-brand-greenDeep disabled:opacity-60" data-testid="ai-send">
            {typing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
      {!embedded && <AiTools />}
    </div>
  );
}
